import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Award, Shield } from 'lucide-react';
import Logo from './Logo';

/**
 * Premium Navigation Component
 * Luxury-grade navigation with sophisticated styling and professional interactions
 * Designed for high-end development firm positioning with advanced UX patterns
 */
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);
  const touchStartY = useRef(null);

  // Enhanced scroll behavior with hide/show functionality
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollThreshold = 100;
    
    // Determine if navbar should have background
    setScrolled(currentScrollY > 20);
    
    // Smart hide/show based on scroll direction (luxury UX pattern)
    if (currentScrollY > scrollThreshold) {
      if (currentScrollY > lastScrollY && currentScrollY > 200) {
        // Scrolling down - hide nav
        setIsVisible(false);
      } else {
        // Scrolling up - show nav
        setIsVisible(true);
      }
    } else {
      // Always show when near top
      setIsVisible(true);
    }
    
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    let ticking = false;
    
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', scrollHandler);
  }, [handleScroll]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Enhanced mobile menu management with targeted scroll locking
  useEffect(() => {
    if (isMenuOpen) {
      // Store current scroll position for restoration
      const scrollY = window.scrollY;
      
      // Prevent background scroll while allowing menu scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll position and remove locks
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMenuOpen]);

  // Handle touch events for mobile menu - only lock at bottom
  const handleTouchStart = useCallback((e) => {
    if (isMenuOpen && mobileMenuRef.current) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, [isMenuOpen]);

  const handleTouchMove = useCallback((e) => {
    if (isMenuOpen && touchStartY.current !== null && mobileMenuRef.current) {
      const menuElement = mobileMenuRef.current;
      const scrollableContent = menuElement.querySelector('.mobile-menu-content');
      
      if (scrollableContent) {
        const isAtBottom = scrollableContent.scrollTop + scrollableContent.clientHeight >= scrollableContent.scrollHeight - 10;
        const touchY = e.touches[0].clientY;
        const touchDiff = touchY - touchStartY.current;
        
        // Only prevent scroll when at bottom and trying to scroll down further
        if (isAtBottom && touchDiff < -30) {
          e.preventDefault();
          setIsMenuOpen(false);
        }
      }
    }
  }, [isMenuOpen]);

  // Add global touch event listeners with proper handling
  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      
      return () => {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isMenuOpen, handleTouchStart, handleTouchMove]);

  // Smooth scroll to top with luxury easing
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Enhanced navigation with luxury UX
  const handleNavigation = useCallback((path) => {
    if (location.pathname === path) {
      scrollToTop();
    } else {
      navigate(path);
      setTimeout(scrollToTop, 100);
    }
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname, navigate, scrollToTop]);

  // Close mobile menu
  const closeMobileMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Refined luxury navigation structure
  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'Legacy' },
    { path: '/services', label: 'Craft' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/process', label: 'Method' },
    { path: '/contact', label: 'Contact' }
  ];

  // Luxury dropdown component
  const Dropdown = ({ items, isOpen, onClose }) => (
    <div className={`absolute top-full left-0 mt-4 w-80 bg-white/98 backdrop-blur-2xl rounded-3xl shadow-2xl border border-amber-200/30 transform transition-all duration-500 ease-luxury ${
      isOpen 
        ? 'opacity-100 translate-y-0 pointer-events-auto scale-100' 
        : 'opacity-0 -translate-y-6 pointer-events-none scale-95'
    }`}>
      <div className="absolute -top-2 left-8 w-4 h-4 bg-white/98 border-l border-t border-amber-200/30 transform rotate-45"></div>
      <div className="p-6 relative">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 rounded-t-3xl opacity-80"></div>
        {items.map((item, idx) => (
          <button
            key={idx}
            onClick={() => {
              handleNavigation(item.path);
              onClose();
            }}
            className="w-full text-left px-6 py-4 rounded-2xl hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all duration-400 group flex items-start space-x-4 border border-transparent hover:border-orange-200/50 hover:shadow-lg"
          >
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center text-2xl group-hover:from-orange-200 group-hover:to-amber-200 transition-all duration-400 group-hover:scale-110 group-hover:rotate-3">
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900 group-hover:text-orange-700 transition-colors duration-300 text-lg mb-1">
                {item.label}
              </div>
              <div className="text-sm text-gray-600 group-hover:text-orange-600 transition-colors duration-300 leading-relaxed">
                {item.desc}
              </div>
            </div>
            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <ChevronDown className="h-4 w-4 text-orange-500 transform -rotate-90" />
            </div>
          </button>
        ))}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            Luxury structural solutions since 2018
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Architectural Luxury Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-700 ease-out ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      } ${
        scrolled 
          ? 'bg-gray-900/98 backdrop-blur-2xl shadow-2xl border-b border-amber-500/20' 
          : 'bg-gray-900/95 backdrop-blur-xl'
      }`}>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8">
          {/* Architectural Layout Structure */}
          <div className="py-6">
            
            {/* Top Row: Phone + Credentials */}
            <div className="flex justify-between items-center mb-6">
              {/* Left: Premium Phone */}
              <a 
                href="tel:602-448-1723"
                className="flex items-center space-x-3 text-amber-400 hover:text-amber-300 transition-all duration-300 group"
              >
                <div className="w-5 h-5 border border-amber-400 rounded-sm flex items-center justify-center group-hover:border-amber-300 transition-colors duration-300">
                  <Phone className="h-3 w-3" />
                </div>
                <span className="font-light tracking-widest text-sm">602.448.1723</span>
              </a>
              
              {/* Right: Luxury Credentials */}
              <div className="hidden lg:flex items-center space-x-6 text-xs font-light tracking-widest text-amber-400">
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                  <span>CR-7 LICENSED</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                  <span>SINCE 2018</span>
                </div>
              </div>
            </div>
            
            {/* Center Row: Logo */}
            <div className="flex justify-center mb-6">
              <Link 
                to="/" 
                className="transform hover:scale-105 transition-all duration-500 relative group" 
                onClick={scrollToTop}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 to-amber-400/10 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-sm"></div>
                <Logo size="large" />
              </Link>
            </div>
            
            {/* Architectural Divider Beam */}
            <div className="relative mb-6">
              <div className="h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-px bg-gradient-to-r from-amber-400 to-amber-500 shadow-lg shadow-amber-500/50"></div>
            </div>
            
            {/* Centered Architectural Navigation */}
            <div className="hidden xl:flex justify-center">
              <div className="flex items-center space-x-12">
                {navItems.map(item => (
                  <div key={item.path} className="relative dropdown-container">
                    <button
                      onClick={() => {
                        if (item.dropdown) {
                          setActiveDropdown(activeDropdown === item.path ? null : item.path);
                        } else {
                          handleNavigation(item.path);
                        }
                      }}
                      className={`relative group px-4 py-3 transition-all duration-500 ${
                        location.pathname === item.path 
                          ? 'text-amber-300' 
                          : 'text-white hover:text-amber-400'
                      }`}
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      <span className="text-lg font-light tracking-widest uppercase">
                        {item.label}
                      </span>
                      
                      {item.dropdown && (
                        <ChevronDown className={`inline-block ml-2 h-3 w-3 transition-all duration-300 ${
                          activeDropdown === item.path ? 'rotate-180' : ''
                        }`} />
                      )}
                      
                      {/* Luxury hover underline */}
                      <div className={`absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-amber-400 to-amber-500 transform origin-left transition-all duration-500 ${
                        location.pathname === item.path 
                          ? 'scale-x-100 opacity-100 shadow-lg shadow-amber-500/50' 
                          : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100'
                      }`}></div>
                      
                      {/* Subtle glow effect */}
                      <div className={`absolute inset-0 bg-amber-400/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                        location.pathname === item.path ? 'opacity-20' : ''
                      }`}></div>
                    </button>
                    
                    {/* Enhanced Dropdown Menu */}
                    {item.dropdown && (
                      <Dropdown 
                        items={item.dropdown}
                        isOpen={activeDropdown === item.path}
                        onClose={() => setActiveDropdown(null)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="xl:hidden absolute top-4 right-6">
              <button
                className="relative p-3 text-amber-400 hover:text-amber-300 transition-all duration-500 rounded-lg hover:bg-amber-400/10 transform hover:scale-110"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle navigation menu"
              >
                <div className="relative w-6 h-6">
                  <div className={`absolute inset-0 transform transition-all duration-500 ${
                    isMenuOpen ? 'rotate-180 opacity-0 scale-75' : 'rotate-0 opacity-100 scale-100'
                  }`}>
                    <Menu className="w-6 h-6" />
                  </div>
                  <div className={`absolute inset-0 transform transition-all duration-500 ${
                    isMenuOpen ? 'rotate-0 opacity-100 scale-100' : '-rotate-180 opacity-0 scale-75'
                  }`}>
                    <X className="w-6 h-6" />
                  </div>
                </div>
                
                {/* Premium button background */}
                <div className={`absolute inset-0 rounded-lg bg-gradient-to-r from-amber-500/20 to-amber-400/20 opacity-0 transition-opacity duration-300 ${
                  isMenuOpen ? 'opacity-100' : ''
                }`}></div>
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Mobile Navigation with Full Background Blur */}
        <div className={`xl:hidden fixed inset-0 z-50 transition-all duration-500 ease-out ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}>
          
          {/* Full Screen Background Overlay with Strong Blur */}
          <div 
            className={`fixed inset-0 bg-black/70 backdrop-blur-2xl transition-all duration-500 ${
              isMenuOpen ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ 
              backdropFilter: 'blur(20px) saturate(1.5)',
              WebkitBackdropFilter: 'blur(20px) saturate(1.5)'
            }}
            onClick={closeMobileMenu}
          />
          
          {/* Mobile Menu Panel */}
          <div 
            ref={mobileMenuRef}
            className={`relative z-10 bg-gray-900/95 backdrop-blur-2xl shadow-2xl transition-all duration-500 ease-out ${
              isMenuOpen ? 'translate-y-0' : '-translate-y-full'
            }`}
            style={{ 
              maxHeight: '85vh',
              borderBottomLeftRadius: '1.5rem',
              borderBottomRightRadius: '1.5rem',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)'
            }}
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-amber-500/20">
              <Link 
                to="/" 
                className="transform hover:scale-105 transition-all duration-300" 
                onClick={closeMobileMenu}
              >
                <Logo size="medium" />
              </Link>
              
              <button
                onClick={closeMobileMenu}
                className="p-2 text-amber-400 hover:text-amber-300 transition-colors duration-300 hover:bg-amber-400/10 rounded-lg"
                aria-label="Close navigation menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div 
              className="mobile-menu-content overflow-y-auto"
              style={{ 
                maxHeight: 'calc(85vh - 80px)',
                overscrollBehavior: 'contain'
              }}
            >
              <div className="p-6 space-y-6">
                
                {/* Elegant top accent beam */}
                <div className="relative">
                  <div className="h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-amber-400 to-amber-500 shadow-lg shadow-amber-500/50"></div>
                </div>
                
                {/* Premium Mobile Contact */}
                <div className="flex items-center justify-between p-6 bg-gray-800/50 rounded-xl border border-amber-500/20">
                  <div>
                    <div className="text-xs text-amber-400 font-light tracking-widest uppercase mb-1">Direct Line</div>
                    <a 
                      href="tel:602-448-1723" 
                      className="text-xl font-light text-white hover:text-amber-300 transition-colors duration-300" 
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      onClick={closeMobileMenu}
                    >
                      602.448.1723
                    </a>
                    <div className="text-xs text-gray-400 mt-1 font-light tracking-wide">Arizona's Premier Framers</div>
                  </div>
                  <div className="w-12 h-12 border border-amber-400 rounded-sm flex items-center justify-center">
                    <Phone className="h-6 w-6 text-amber-400" />
                  </div>
                </div>
                
                {/* Architectural Mobile Navigation Links */}
                <div className="space-y-2">
                  {navItems.map((item, index) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`block w-full text-center py-6 transition-all duration-500 relative group rounded-xl ${
                        location.pathname === item.path 
                          ? 'text-amber-300 bg-amber-500/10 border border-amber-400/30' 
                          : 'text-white hover:text-amber-400 hover:bg-amber-500/5 border border-transparent hover:border-amber-500/20'
                      }`}
                      style={{ 
                        fontFamily: 'Playfair Display, serif',
                        animationDelay: `${index * 0.1}s`
                      }}
                    >
                      <span className="text-lg font-light tracking-widest uppercase">
                        {item.label}
                      </span>
                      
                      {/* Luxury mobile underline */}
                      <div className={`absolute bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-px bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-500 ${
                        location.pathname === item.path 
                          ? 'opacity-100 shadow-lg shadow-amber-500/50' 
                          : 'opacity-0 group-hover:opacity-100'
                      }`}></div>
                    </button>
                  ))}
                </div>
                
                {/* Mobile credentials - Bottom Lock Section */}
                <div className="pt-6 mt-6 border-t border-amber-500/20 bg-gray-800/30 rounded-xl p-4">
                  <div className="flex items-center justify-center space-x-6 text-xs text-amber-400 font-light tracking-wide">
                    <div className="flex items-center space-x-2">
                      <Award className="h-3 w-3" />
                      <span>CR-7 Licensed</span>
                    </div>
                    <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                    <div className="flex items-center space-x-2">
                      <Shield className="h-3 w-3" />
                      <span>Since 2018</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Architectural Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-800 z-40">
        <div 
          className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-100 ease-out shadow-lg shadow-amber-500/50"
          style={{
            width: `${Math.min((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100, 100)}%`
          }}
        />
        <div 
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-white/30 to-transparent opacity-40"
          style={{
            width: `${Math.min((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100, 100)}%`
          }}
        />
      </div>
    </>
  );
};

export default Navigation;